import React, { useState, useEffect } from 'react';
import {
  Box,
  Frame,
  Typography,
  Loader,
  Button
} from 'components';
import { useParams, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useContent } from 'utils';
import firebase from 'utils/firebase';
import { makeStyles } from '@material-ui/core/styles';
import { Avatar, Card, CardContent, Chip, Divider, Paper } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const useStyles = makeStyles((theme) => ({
  avatar: {
    width: theme.spacing(15),
    height: theme.spacing(15),
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[3]
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 500,
    position: 'relative',
    paddingBottom: theme.spacing(1),
    '&:after': {
      content: '""',
      position: 'absolute',
      left: 0,
      bottom: 0,
      width: 60,
      height: 4,
      backgroundColor: theme.palette.primary.main
    }
  },
  chip: {
    margin: theme.spacing(0.5),
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: theme.shadows[2]
    }
  },
  section: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    boxShadow: theme.shadows[1],
    borderRadius: theme.shape.borderRadius,
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[3]
    }
  },
  roleCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[2]
    }
  },
  experienceChip: {
    fontWeight: 500,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    }
  }
}));

const SharedFreelancerShow = () => {
  const classes = useStyles();
  const { groupId, freelancerId } = useParams();
  const [freelancer, setFreelancer] = useState(null);
  const [publicGroup, setPublicGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { title } = useContent();
  const history = useHistory();

  useEffect(() => {
    const fetchFreelancer = async () => {
      try {
        setLoading(true);
        
        // Get the document from publicGroups collection
        const doc = await firebase.firestore()
          .collection('publicGroups')
          .doc(groupId)
          .get();
        
        if (!doc.exists) {
          setError('This shared search does not exist or has expired.');
          setLoading(false);
          return;
        }
        
        const data = doc.data();
        
        // Check if the shared search has expired
        let expirationDate;
        try {
          if (data.expiresAt) {
            if (typeof data.expiresAt.toDate === 'function') {
              expirationDate = data.expiresAt.toDate();
            } else if (data.expiresAt instanceof Date) {
              expirationDate = data.expiresAt;
            } else if (typeof data.expiresAt === 'object' && data.expiresAt.seconds) {
              expirationDate = new Date(data.expiresAt.seconds * 1000);
            } else if (typeof data.expiresAt === 'number') {
              expirationDate = new Date(data.expiresAt);
            } else if (typeof data.expiresAt === 'string') {
              expirationDate = new Date(data.expiresAt);
            }
          }
          
          if (expirationDate && expirationDate < new Date()) {
            setError('This shared search has expired.');
            setLoading(false);
            return;
          }
        } catch (dateError) {
          console.error('Error parsing expiration date:', dateError);
        }
        
        setPublicGroup(data);
        
        // Find the freelancer by ID
        const found = data.users.find(user => user.id === freelancerId);
        if (!found) {
          setError('Freelancer not found in this shared search.');
          setLoading(false);
          return;
        }
        
        setFreelancer(found);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching freelancer:', err);
        setError('Error loading freelancer data. Please try again later.');
        setLoading(false);
      }
    };
    
    if (groupId && freelancerId) {
      fetchFreelancer();
    } else {
      setError('Invalid URL parameters');
      setLoading(false);
    }
  }, [groupId, freelancerId]);

  const handleBackClick = () => {
    history.push(`/shared/${groupId}`);
  };
  
  // Format expiration date for display
  let formattedDate = '';
  try {
    if (publicGroup?.expiresAt) {
      let expirationDate;
      if (typeof publicGroup.expiresAt.toDate === 'function') {
        expirationDate = publicGroup.expiresAt.toDate();
      } else if (publicGroup.expiresAt instanceof Date) {
        expirationDate = publicGroup.expiresAt;
      } else if (typeof publicGroup.expiresAt === 'object' && publicGroup.expiresAt.seconds) {
        expirationDate = new Date(publicGroup.expiresAt.seconds * 1000);
      } else if (typeof publicGroup.expiresAt === 'number') {
        expirationDate = new Date(publicGroup.expiresAt);
      } else if (typeof publicGroup.expiresAt === 'string') {
        expirationDate = new Date(publicGroup.expiresAt);
      }
      
      if (expirationDate) {
        formattedDate = new Date(expirationDate).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    }
  } catch (error) {
    console.error('Error parsing date for formatting:', error);
  }

  return (
    <Frame maxWidth="md">
      <Helmet>
        <title>{title} | {freelancer ? freelancer.name : 'Freelancer Profile'}</title>
      </Helmet>
      
      {/* Back button */}
      <Box mb={2}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
        >
          Back to search results
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <Loader />
        </Box>
      ) : error ? (
        <Box mt={4} p={3} bgcolor="error.light" borderRadius={1}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      ) : freelancer ? (
        <>
          {/* Shared profile info banner */}
          <Box mb={4} p={2} bgcolor="primary.light" borderRadius={1} boxShadow={1}>
            <Typography variant="body2" align="center" style={{ fontWeight: 500 }}>
              Shared Profile • Link Expires on {formattedDate}
            </Typography>
          </Box>
          
          {/* Profile Header */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            <Avatar 
              src={freelancer.avatar || undefined} 
              alt={freelancer.name}
              className={classes.avatar}
            />
            <Typography variant="h4" gutterBottom>
              {freelancer.name}
            </Typography>
            {freelancer.title && (
              <Typography variant="h6" color="textSecondary" gutterBottom>
                {freelancer.title}
              </Typography>
            )}
            {freelancer.city && (
              <Typography variant="body1" color="textSecondary" gutterBottom>
                {freelancer.city}{freelancer.province ? `, ${freelancer.province}` : ''}
              </Typography>
            )}
            
            {/* No availability information shown in shared profiles */}
          </Box>

          {/* Main Content */}
          <Box>
            {/* Roles Section */}
            {Array.isArray(freelancer.roles) && freelancer.roles.length > 0 && (
              <Paper className={classes.section}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Roles & Experience
                </Typography>
                <Box mt={2}>
                  {freelancer.roles.map((role, index) => {
                    // Extract role information
                    const roleName = typeof role === 'string' ? role : role.name || role.label || 'Role';
                    const experience = typeof role === 'object' ? role.experience || 0 : 
                                     (freelancer.experienceByRole && freelancer.experienceByRole[roleName]) ? 
                                     freelancer.experienceByRole[roleName] : 0;
                    
                    // Get skills from multiple possible sources
                    let skills = [];
                    
                    // First try to get skills from the role object
                    if (typeof role === 'object' && Array.isArray(role.skills) && role.skills.length > 0) {
                      skills = role.skills;
                    } 
                    // If no skills on role but we have a skills array on the user, use that
                    else if (Array.isArray(freelancer.skills) && freelancer.skills.length > 0) {
                      skills = freelancer.skills;
                    }
                    
                    return (
                      <Box 
                        key={index} 
                        className={classes.roleCard}
                        bgcolor={index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'white'}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                            {roleName}
                          </Typography>
                          <Chip 
                            label={`${experience} ${experience === 1 ? 'year' : 'years'}`}
                            className={classes.experienceChip}
                            size="small"
                          />
                        </Box>
                        
                        {skills.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              Skills:
                            </Typography>
                            <Box display="flex" flexWrap="wrap">
                              {skills.map((skill, skillIndex) => (
                                <Chip
                                  key={skillIndex}
                                  label={skill}
                                  size="small"
                                  className={classes.chip}
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        {typeof role === 'object' && role.industry && (
                          <Typography variant="body2" color="textSecondary" mt={1}>
                            Industry: {role.industry}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            )}

            {/* Titles section removed */}

            {/* Union Status */}
            {freelancer.union && (
              <Paper className={classes.section}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Union Status
                </Typography>
                <Typography variant="body1">
                  {freelancer.union}
                </Typography>
              </Paper>
            )}
            
            {/* Diversity Tags */}
            {Array.isArray(freelancer.diversityTags) && freelancer.diversityTags.length > 0 && (
              <Paper className={classes.section}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Diversity
                </Typography>
                <Box display="flex" flexWrap="wrap" mt={1}>
                  {freelancer.diversityTags.map((tag, index) => (
                    <Chip 
                      key={index}
                      label={tag}
                      className={classes.chip}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Paper>
            )}
            
            {/* Industries */}
            {Array.isArray(freelancer.industries) && freelancer.industries.length > 0 && (
              <Paper className={classes.section}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Industries
                </Typography>
                <Box display="flex" flexWrap="wrap" mt={1}>
                  {freelancer.industries.map((industry, index) => (
                    <Chip 
                      key={index}
                      label={typeof industry === 'string' ? industry : industry.name || 'Industry'}
                      className={classes.chip}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Paper>
            )}
            
            {/* Sign Up Banner */}
            <Box mt={4} p={3} bgcolor="#f0f8ff" borderRadius={1} textAlign="center">
              <Typography variant="h6" gutterBottom>
                Want to see more details and contact information?
              </Typography>
              <Typography variant="body1" paragraph>
                Sign up for a full account to access the complete freelancer database.
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                href="/signup"
              >
                Create an Account
              </Button>
            </Box>
          </Box>
        </>
      ) : (
        <Typography align="center">
          Freelancer not found
        </Typography>
      )}
    </Frame>
  );
};

export default SharedFreelancerShow;
